import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { SpinnerComponent } from '../spinner/spinner.component';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

@Component({
    selector: 'pvp-button',
    imports: [CommonModule, SpinnerComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class]': 'hostClass()',
    },
    template: `
    <button
      [type]="type()"
      [disabled]="disabled() || loading()"
      [class]="buttonClass()"
      [attr.aria-label]="ariaLabel() || null"
      [attr.aria-pressed]="ariaPressed() === null ? null : (ariaPressed() ? 'true' : 'false')"
      [attr.aria-busy]="loading() ? 'true' : null"
      (click)="clicked.emit($event)"
    >
      @if (loading()) {
        <!-- Decorativo: sin él, el texto sr-only del spinner se sumaría al nombre
             accesible del botón y anidaría un role=status dentro del role=button. -->
        <pvp-spinner size="sm" [decorative]="true" />
      }
      <ng-content />
    </button>
  `
})
export class ButtonComponent {
  readonly variant = input<ButtonVariant>('primary');
  readonly size    = input<ButtonSize>('md');
  readonly type    = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false);
  readonly loading  = input(false);
  readonly fullWidth = input(false);
  /**
   * Nombre accesible del botón NATIVO interno (aria-label). Necesario cuando
   * el texto visible se repite entre filas y el lector de pantalla necesita
   * contexto ("Confirmar propuesta de Ana" vs. "Confirmar" a secas). Un
   * aria-label en el host `<pvp-button>` NO llega al rol button interno.
   */
  readonly ariaLabel = input<string>('');
  /**
   * Estado de un botón TOGGLE (aria-pressed en el `<button>` NATIVO interno).
   * `null` = el botón no es un toggle (no emite aria-pressed); `true`/`false` =
   * presionado o no (ambos estados se emiten, como pide WAI-ARIA para toggles).
   * Igual que `ariaLabel`: un `[attr.aria-pressed]` en el host `<pvp-button>` NO
   * llega al rol button interno — hay que pasarlo por este input.
   */
  readonly ariaPressed = input<boolean | null>(null);

  readonly clicked = output<MouseEvent>();

  protected buttonClass(): string {
    const base = 'inline-flex items-center justify-center gap-2 rounded-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50';

    const variants: Record<ButtonVariant, string> = {
      primary:   'bg-primary text-primary-contrast hover:bg-primary-strong',
      secondary: 'border border-border-strong bg-surface-raised text-text hover:bg-surface-raised hover:border-text-subtle',
      danger:    'bg-danger-tint text-danger hover:bg-danger hover:text-primary-contrast',
      ghost:     'text-text-muted hover:bg-surface-raised hover:text-text',
      link:      'text-primary underline-offset-4 hover:underline hover:text-primary-strong p-0',
    };

    const sizes: Record<ButtonSize, string> = {
      sm: 'h-8 px-3 text-xs',
      md: 'h-10 px-4 text-sm',
      lg: 'h-12 px-6 text-base',
    };

    const fw = this.fullWidth() ? 'w-full' : '';

    return [base, variants[this.variant()], sizes[this.size()], fw].join(' ');
  }

  protected hostClass(): string {
    return this.fullWidth() ? 'block' : 'inline-block';
  }
}
